/**
 * PLAYBACK RATE — the arithmetic that makes the speed selector do something.
 *
 * The selector used to be inert. It set `speed` in playerStore, the store recorded it,
 * and nothing read it: a 3:12 track took 3:12 at 1× and took 3:12 at 2×. That is the
 * "fake speed indicator" the brief rules out, so `speed` now drives the clock.
 *
 * WHAT "PLAYBACK" IS IN THIS APP, precisely, because it decides what this module can
 * honestly claim. There is no <audio> element and no audio file — see the header of
 * src/stores/playerStore.ts. Playback is a 1Hz interval advancing `elapsed`, and every
 * position on screen (the clock, the seek fill, the timeline playhead, the queue
 * readout) is rendered from it. So speed scales that interval, and the effect is real
 * and observable: at 2× a 3:12 track finishes in 1:36 of wall-clock time, the clock
 * counts twice as fast, and the playhead crosses the timeline twice as quickly.
 *
 * What it does NOT do is change a pitch or a sample rate, because there is no sound to
 * change. When an audio element lands, `tickIntervalMs` stops being needed and the same
 * `speed` value goes to `audio.playbackRate` — one assignment, in the same store. The
 * selector is wired to the real thing at that point without changing shape.
 *
 * TWO NUMBERS, not one, and they are not interchangeable:
 *
 *   - `tickIntervalMs` is how often the clock advances by one song-second. It goes DOWN
 *     as speed goes up.
 *   - `smoothingSeconds` is how long a CSS transition should take to cover the distance
 *     one tick moves. It is the same duration expressed in seconds, and it exists
 *     separately because getting it wrong is visible: a fill transitioning over 1s while
 *     the value jumps every 500ms arrives late and never catches up, so the bar reads
 *     half a second behind the clock beside it at 2×.
 *
 * Pure and dependency-free, so both are unit-testable — see __tests__/playback.test.ts.
 */

/** One song-second per second, the unscaled tick. */
export const BASE_TICK_MS = 1000;

/**
 * Bounds on the multiplier.
 *
 * `speed` is a plain number in the store and `setSpeed` does not validate it, so a
 * future control — or a restored preference from a build with a different list — can
 * put anything in it. Zero is the one that matters: `1000 / 0` is Infinity, which
 * `setInterval` coerces to 0 and fires a tick every frame, freezing the tab. Clamping
 * here rather than in the store keeps the store a plain record of what was chosen and
 * puts the guard next to the division that needs it.
 *
 * The range is wider than PLAYBACK_SPEEDS so that adding 0.75× or 3× to the selector
 * needs no change here.
 */
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 4;

/** Clamps a speed multiplier into the supported range. Non-finite input falls back to 1×. */
export function clampSpeed(speed: number): number {
  if (!Number.isFinite(speed) || speed <= 0) return 1;
  return Math.min(MAX_SPEED, Math.max(MIN_SPEED, speed));
}

/**
 * How often the simulated clock should advance one song-second, in milliseconds.
 *
 * Rounded to a whole millisecond because that is the resolution setInterval has, and
 * rounding here rather than leaving a fraction keeps the value the tests assert on
 * identical to the value the timer receives.
 */
export function tickIntervalMs(speed: number): number {
  return Math.round(BASE_TICK_MS / clampSpeed(speed));
}

/**
 * How long a position transition should take to cover one tick, in seconds.
 *
 * The CSS-facing twin of `tickIntervalMs`. Returned as a number so callers build their
 * own declaration — `width ${smoothingSeconds(speed)}s linear` — rather than this
 * module knowing about properties it cannot see.
 */
export function smoothingSeconds(speed: number): number {
  return tickIntervalMs(speed) / 1000;
}

/**
 * IS THE PLAYER ON THIS EXACT QUEUE? — "is this playlist the thing playing".
 *
 * WHY IT EXISTS. A playlist's Play button has to become a Pause button once that playlist
 * is running, and "the loaded song is one of these tracks" cannot decide it: two playlists
 * can share a song, so that test would have both of them offering to pause a queue only one
 * of them built. What identifies a playlist's playback is the ORDER the queue was started
 * in, which is exactly what usePlaylists.playPlaylist puts in the store.
 *
 * WHY IT IS HERE rather than inline. The playlist page computed this for its header button
 * while the playlists LIST computed nothing at all — which is the defect: the card's control
 * was a permanent Play glyph that restarted the queue. Two surfaces asking the same question
 * have to ask it the same way, or they disagree about whether a playlist is playing.
 *
 * A false result once the playlist is edited mid-playback is CORRECT, not a limitation: at
 * that point the queue really has stopped being this playlist's order, and the button
 * correctly goes back to offering a fresh Play.
 *
 * An empty `ids` is never a match. Nothing can be playing a playlist with no tracks, and an
 * empty queue compares equal to an empty playlist by length — so this is the guard that
 * stops an empty playlist's disabled button from claiming to be playing.
 */
export function queueMatches(queue: readonly string[], ids: readonly string[]): boolean {
  if (ids.length === 0) return false;
  if (queue.length !== ids.length) return false;
  return ids.every((id, i) => queue[i] === id);
}
